/** Create/edit form for an ArtifactPosition. Validated against `createArtifactPositionSchema`. */
import { useForm } from "@refinedev/react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";

import {
  createArtifactPositionSchema,
  type CreateArtifactPositionInput,
} from "@aos/shared";

import { FormShell } from "../skills/_components/FormShell";
import {
  IntCheckboxField,
  NumberField,
  TextField,
} from "../skills/_components/form";

export interface ArtifactPositionFormProps {
  action: "create" | "edit";
}

export function ArtifactPositionForm({ action }: ArtifactPositionFormProps) {
  const form = useForm<CreateArtifactPositionInput>({
    resolver: zodResolver(createArtifactPositionSchema),
    refineCoreProps: { resource: "artifact-positions", action },
  });

  const {
    refineCore: { onFinish, formLoading },
    handleSubmit,
  } = form;

  return (
    <FormShell
      title={action === "create" ? "New Artifact Position" : "Edit Artifact Position"}
      resource="artifact-positions"
      isSubmitting={formLoading}
      onSubmit={handleSubmit((values) => onFinish(values))}
    >
      <TextField
        form={form}
        name="artifact_id"
        label="Artifact ID"
        placeholder="art_…"
      />
      <TextField
        form={form}
        name="process_id"
        label="Contour Package ID"
        placeholder="proc_… (contour package)"
        description="The contour package this artifact is positioned within."
      />
      <TextField
        form={form}
        name="phase_id"
        label="Phase ID / Mode"
        placeholder="phase_… (strong) or a mode name (weak)"
      />
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <NumberField
          form={form}
          name="membership_score"
          label="Membership Score"
          placeholder="1.0"
        />
        <NumberField
          form={form}
          name="confidence_score"
          label="Confidence Score"
          placeholder="0.0 – 1.0"
        />
      </div>
      <IntCheckboxField
        form={form}
        name="is_primary"
        label="Primary position"
        description="On for a strong contour's single phase; off for weak-contour modes."
      />
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <TextField
          form={form}
          name="entered_at"
          label="Entered At"
          placeholder="ISO-8601 (optional)"
        />
        <TextField
          form={form}
          name="last_event_id"
          label="Last Event ID"
          placeholder="evt_… (optional)"
        />
      </div>
    </FormShell>
  );
}

export default ArtifactPositionForm;
